const PAYMENT_FIELDS_CONFIG = [
    {
        name: 'downPayment',
        selector: '.financial-lease-input[data-default-down-payment-percentage]',
        defaultKey: 'defaultDownPaymentPercentage',
        maxKey: 'maxDownPaymentPercentage',
        onInvalid: 'default',
    },
    {
        name: 'balloonPayment',
        selector: '.financial-lease-input[data-default-balloon-payment-percentage]',
        defaultKey: 'defaultBalloonPaymentPercentage',
        maxKey: 'maxBalloonPaymentPercentage',
        onInvalid: 'maxAllowed',
    },
];

export class FinancialLeasePaymentFields {
    #root;

    #grossInvestmentInput;

    #formatter;

    #minGrossInvestmentValue = 1000;

    #onGrossInvestmentChange;

    #userModifiedFields = new Set();

    /**
     * @param {Element} root
     * @param {() => void} onGrossInvestmentChange
     */
    constructor(root, onGrossInvestmentChange = () => {}) {
        this.#root = root;
        this.#grossInvestmentInput = root.querySelector('[name*="grossInvestment"]');
        this.#onGrossInvestmentChange = onGrossInvestmentChange;
        this.#formatter = new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    get #grossInvestment() {
        return this.#grossInvestmentInput ? this.#parseEuroValue(this.#grossInvestmentInput.value) : 0;
    }

    setup() {
        this.#bindRangeSliders();
        this.#bindGrossInvestmentChanges();
        this.#updateSliderMaxValues();
        this.#applyDefaultsCorrectly(true);
        this.#updateSliderMaxValues();
    }

    #bindRangeSliders() {
        this.#root.querySelectorAll('.financial-lease-range').forEach((rangeInput) => {
            const targetId = rangeInput.dataset.target;
            const targetInput = this.#root.querySelector(`#${targetId}`);

            if (targetInput) {
                rangeInput.addEventListener('input', () => {
                    this.#syncRangeToInput(rangeInput, targetInput);
                    this.#userModifiedFields.add(targetInput);
                    this.#updateSliderMaxValues(targetInput);
                });
            }
        });

        this.#root.querySelectorAll('.financial-lease-input').forEach((textInput) => {
            const targetId = textInput.dataset.target;
            const targetRange = this.#root.querySelector(`#${targetId}`);

            if (targetRange) {
                textInput.addEventListener('input', () => {
                    this.#syncInputToRange(textInput, targetRange);
                    this.#userModifiedFields.add(textInput);
                    this.#updateSliderMaxValues(textInput);
                });
            }
        });
    }

    #bindGrossInvestmentChanges() {
        if (!this.#grossInvestmentInput || this.#grossInvestmentInput.readOnly) {
            return;
        }

        this.#grossInvestmentInput.addEventListener('input', () => {
            this.#onGrossInvestmentChange();
            this.#updateSliderMaxValues();
            this.#applyDefaultsCorrectly();
            this.#updateSliderMaxValues();
        });
    }

    #applyDefaultsCorrectly(respectPrePopulatedValues = false) {
        for (const field of PAYMENT_FIELDS_CONFIG) {
            const input = this.#root.querySelector(field.selector);

            if (!input) {
                console.error('FinancialLeasePaymentFields | Expected selector not found in DOM', {
                    selector: field.selector,
                    rootId: this.#root.id,
                });
                continue;
            }

            const rangeInput = this.#root.querySelector(`.financial-lease-range[data-target="${input.id}"]`);
            const parsedInputValue = this.#parseEuroValue(input.value);

            if (this.#grossInvestment < this.#minGrossInvestmentValue) {
                if (rangeInput) rangeInput.value = 0;
                input.value = '0,00';
                this.#userModifiedFields.delete(input);
                continue;
            }

            if (this.#userModifiedFields.has(input)) {
                const maxValue = Number(rangeInput?.max) || 0;
                if (maxValue <= 0 || parsedInputValue <= maxValue) {
                    continue;
                }

                this.#userModifiedFields.delete(input);

                if (field.onInvalid === 'maxAllowed') {
                    if (rangeInput) rangeInput.value = maxValue;
                    input.value = this.#formatEuroValue(maxValue);
                    continue;
                }
            }

            let targetValue;
            if (respectPrePopulatedValues && parsedInputValue > 0) {
                const maxValue = Number(rangeInput?.max) || 0;
                targetValue = maxValue > 0 ? Math.min(parsedInputValue, maxValue) : parsedInputValue;
            } else {
                const defaultPercentage = Number(input.dataset[field.defaultKey] || rangeInput?.dataset?.[field.defaultKey]);
                targetValue = this.#grossInvestment * defaultPercentage;
            }

            if (rangeInput) rangeInput.value = targetValue;
            input.value = this.#formatEuroValue(targetValue);
        }
    }

    #updateSliderMaxValues(changedInput = null) {
        if (!this.#grossInvestmentInput) {
            return;
        }

        const paymentFields = PAYMENT_FIELDS_CONFIG.map((field) => {
            const input = this.#root.querySelector(field.selector);
            const rangeInput = input ? this.#root.querySelector(`.financial-lease-range[data-target="${input.id}"]`) : null;

            return input && rangeInput ? { field, input, rangeInput } : null;
        }).filter((field) => field !== null);
        const changedPaymentField = changedInput ? paymentFields.find(({ input }) => input === changedInput) : null;

        if (changedPaymentField) {
            const maxPercentage = Number(changedPaymentField.input.dataset[changedPaymentField.field.maxKey]);
            const maxValue = this.#grossInvestment * maxPercentage;

            if (this.#parseEuroValue(changedPaymentField.input.value) > maxValue) {
                changedPaymentField.rangeInput.value = maxValue;
                changedPaymentField.input.value = this.#formatEuroValue(maxValue);
                this.#userModifiedFields.delete(changedPaymentField.input);
            }
        }

        const updateMaxValues = () => {
            for (const paymentField of paymentFields) {
                const { field, input, rangeInput } = paymentField;
                const maxPercentage = Number(input.dataset[field.maxKey]);
                let maxValue = this.#grossInvestment * maxPercentage;

                if (this.#grossInvestment >= this.#minGrossInvestmentValue) {
                    const otherPaymentField = paymentFields.find((candidate) => candidate !== paymentField);
                    const otherPayment = otherPaymentField ? this.#parseEuroValue(otherPaymentField.input.value) : 0;
                    maxValue = Math.min(maxValue, Math.max(0, this.#grossInvestment - otherPayment));
                }

                if (this.#grossInvestment <= 0) {
                    maxValue = 0;
                }

                rangeInput.max = maxValue;
                input.max = maxValue;
                rangeInput.disabled = this.#grossInvestment < this.#minGrossInvestmentValue;
                input.disabled = this.#grossInvestment < this.#minGrossInvestmentValue;
            }
        };

        updateMaxValues();

        if (!changedPaymentField) {
            return;
        }

        const paymentFieldToAdjust = paymentFields.find((paymentField) => paymentField !== changedPaymentField);
        if (!paymentFieldToAdjust) {
            return;
        }

        const maxValue = Number(paymentFieldToAdjust.rangeInput.max) || 0;
        if (this.#parseEuroValue(paymentFieldToAdjust.input.value) > maxValue) {
            paymentFieldToAdjust.rangeInput.value = maxValue;
            paymentFieldToAdjust.input.value = this.#formatEuroValue(maxValue);
            updateMaxValues();
            changedPaymentField.rangeInput.value = this.#parseEuroValue(changedPaymentField.input.value);
        }
    }

    #parseEuroValue(value) {
        return (
            Number(
                String(value)
                    .replace(/[^0-9,]/g, '')
                    .replace(',', '.'),
            ) || 0
        );
    }

    #formatEuroValue(value) {
        return this.#formatter.format(value).replace('€', '').trim();
    }

    #syncRangeToInput(rangeInput, textInput) {
        const value = rangeInput.valueAsNumber ?? 0;
        textInput.value = this.#formatEuroValue(value);
    }

    #syncInputToRange(textInput, rangeInput) {
        rangeInput.value = this.#parseEuroValue(textInput.value);
    }
}
